import { useEffect, useMemo, useRef } from 'react';
import { useAgendas } from '../api/queries';
import type { AgendaSummary, MeetingType } from '../types/agenda';
import './move-dialog.css';

function formatDate(date: string): string {
  // Parse as local midnight so a YYYY-MM-DD string is not shifted a day by UTC.
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function MoveItemDialog({
  open,
  itemTitle,
  currentAgendaId,
  meetingType,
  onSelect,
  onClose,
}: {
  open: boolean;
  itemTitle: string;
  currentAgendaId: string;
  meetingType: MeetingType;
  onSelect: (destId: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { data, isLoading } = useAgendas();

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    else if (!open && dlg.open) dlg.close();
  }, [open]);

  // Only agendas of the same meeting type the user can write to, excluding this
  // one, most-recent first.
  const candidates = useMemo<AgendaSummary[]>(() => {
    if (!data) return [];
    const writable = new Set(data.permissions.writeTypes);
    return data.agendas
      .filter(
        (s) =>
          s.id !== currentAgendaId &&
          s.meetingType === meetingType &&
          writable.has(s.meetingType),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [data, currentAgendaId, meetingType]);

  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) onClose();
  };

  return (
    <dialog ref={ref} className="move-dialog" onClose={onClose} onClick={onClick}>
      <div className="move-dialog-panel">
        <h2 className="move-dialog-title">Move item to…</h2>
        <p className="move-dialog-sub">
          Move “{itemTitle || 'Untitled'}” to another meeting.
        </p>

        <div className="move-dialog-list">
          {isLoading ? (
            <p className="move-dialog-empty">Loading meetings…</p>
          ) : candidates.length === 0 ? (
            <p className="move-dialog-empty">No other meetings of this type to move to.</p>
          ) : (
            candidates.map((s) => (
              <button
                key={s.id}
                className="move-dialog-option"
                onClick={() => onSelect(s.id)}
              >
                <span className="move-dialog-option-title">{s.title || 'Untitled'}</span>
                <span className="move-dialog-option-date">{formatDate(s.date)}</span>
              </button>
            ))
          )}
        </div>

        <div className="move-dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}
